import { createGoogleAds, type GoogleAdsEnvironment } from './googleAds';
import { GOOGLE_ADS_CONFIG } from './googleAdsConfig';
import { createGoogleAnalytics, analyticsQuote, ANALYTICS_QUOTE_PREFIX, type AnalyticsQuote } from './googleAnalytics';
import { GOOGLE_ANALYTICS_CONFIG } from './googleAnalyticsConfig';
import type { CheckoutQuote } from '@workspace/url-source/checkout';

type TagWindow = Window & { dataLayer?: unknown[]; gtag?: (...args: unknown[]) => void; 'ga-disable-G-YKNM2G70M9'?: boolean };
let tracker: ReturnType<typeof createBrowserMeasurement> | null = null;
const DENIED = { analytics_storage: 'denied', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' } as const;

function createBrowserMeasurement() {
  const tagWindow = window as TagWindow;
  let initialized = false;
  let ready = false;
  let loading: Promise<boolean> | null = null;
  let consent = { analytics: false, marketing: false };
  const clearCookies = (pattern: RegExp) => {
    for (const part of document.cookie.split(';')) {
      const name = part.trim().split('=')[0];
      if (!pattern.test(name)) continue;
      for (const domain of ['', location.hostname, '.bukietowa.pl']) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain ? `; Domain=${domain}` : ''}`;
      }
    }
  };
  const env: GoogleAdsEnvironment = {
    managedConsent: true,
    location: () => window.location.href,
    referrer: () => document.referrer,
    now: Date.now,
    storage: { getItem: key => localStorage.getItem(key), setItem: (key, value) => localStorage.setItem(key, value), removeItem: key => localStorage.removeItem(key) },
    command(...args) {
      tagWindow.dataLayer ??= [];
      tagWindow.gtag ??= function () { tagWindow.dataLayer!.push(arguments); };
      tagWindow.gtag(...args);
    },
    loadTag(tagId) {
      if (ready) return Promise.resolve(true);
      if (loading) return loading;
      loading = new Promise<boolean>(resolve => {
        // Strip Stripe redirect secrets before either Google destination loads.
        if (/^\/dziekujemy\/?$/.test(location.pathname)) {
          const clean = new URL(location.href);
          clean.searchParams.delete('payment_intent_client_secret');
          clean.searchParams.delete('setup_intent_client_secret');
          if (clean.href !== location.href) history.replaceState(history.state, '', clean.href);
        }
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
        script.referrerPolicy = 'origin';
        script.onload = () => { ready = true; resolve(true); };
        script.onerror = () => { script.remove(); resolve(false); };
        document.head.appendChild(script);
      }).finally(() => { loading = null; });
      return loading;
    },
    clearAttribution: () => clearCookies(/^_gcl_[A-Za-z0-9_]+$/),
  };
  const ads = createGoogleAds(GOOGLE_ADS_CONFIG, env);
  const analytics = createGoogleAnalytics(GOOGLE_ANALYTICS_CONFIG, { ...env, clearAttribution() {
    clearCookies(/^_ga(?:_[A-Za-z0-9_]+)?$/);
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(ANALYTICS_QUOTE_PREFIX)) sessionStorage.removeItem(key);
      }
    } catch { /* Storage access can be blocked. */ }
  } });
  async function updateConsent(next: { analytics: boolean; marketing: boolean }) {
    consent = next;
    // Also stop autonomous GA requests after withdrawal, while Ads may stay active.
    tagWindow[`ga-disable-${GOOGLE_ANALYTICS_CONFIG.tagId}`] = !next.analytics;
    if (!GOOGLE_ADS_CONFIG.hosts.some(host => host === location.hostname)) return false;
    if (!initialized && (next.analytics || next.marketing)) {
      initialized = true;
      env.command('consent', 'default', DENIED);
      env.command('set', 'ads_data_redaction', true);
      env.command('set', 'allow_ad_personalization_signals', false);
      env.command('js', new Date());
    }
    if (initialized) env.command('consent', 'update', {
      ...DENIED, analytics_storage: next.analytics ? 'granted' : 'denied',
      ad_storage: next.marketing ? 'granted' : 'denied', ad_user_data: next.marketing ? 'granted' : 'denied',
    });
    // Each gate changes synchronously before awaiting the shared library.
    const results = await Promise.all([analytics.updateConsent(next.analytics), ads.updateConsent(next.marketing)]);
    return results.some(Boolean);
  }
  function rememberQuote(id: string, quote: CheckoutQuote) {
    if (!consent.analytics || !/^pi_[A-Za-z0-9]+$/.test(id)) return;
    const safe = analyticsQuote(quote);
    if (!safe) return;
    try { sessionStorage.setItem(ANALYTICS_QUOTE_PREFIX + id, JSON.stringify(safe)); } catch { /* No storage means no item-level purchase measurement. */ }
  }
  function readQuote(id: string): AnalyticsQuote | null {
    if (!consent.analytics || !/^pi_[A-Za-z0-9]+$/.test(id)) return null;
    try {
      const value: unknown = JSON.parse(sessionStorage.getItem(ANALYTICS_QUOTE_PREFIX + id) ?? 'null');
      if (!value || typeof value !== 'object' || !Array.isArray((value as AnalyticsQuote).items)) return null;
      return value as AnalyticsQuote;
    } catch { return null; }
  }
  return { updateConsent, ads, analytics, rememberQuote, readQuote };
}
export function browserGoogleMeasurement() { return tracker ??= createBrowserMeasurement(); }
